<template>
	<!-- iCalendar y JSON: mismo diseño que Mis espacios. Por alojamiento:
	     generar / copiar el enlace iCal y el JSON, descargar el .ics y subir un
	     .ics para bloquear fechas (misma API que antes). -->
	<div class="bk">
		<aside class="bk-side">
			<h1 class="bk-title">iCalendar <span class="bk-grad">{{ $t('nuevo_menu.ic.title_b') }}</span></h1>
			<p class="bk-subtitle">{{ $t('nuevo_menu.ic.sub') }}</p>
			<div class="bk-stats">
				<div class="bk-stat"><p class="bk-stat-n">{{ resumen.total }}</p><p class="bk-stat-l">{{ $t('nuevo_menu.sp.stat_total') }}</p></div>
				<div class="bk-stat bk-stat--ok"><p class="bk-stat-n">{{ resumen.aprobados }}</p><p class="bk-stat-l">{{ $t('nuevo_menu.sp.stat_ok') }}</p></div>
				<div class="bk-stat bk-stat--wait"><p class="bk-stat-n">{{ resumen.en_revision }}</p><p class="bk-stat-l">{{ $t('nuevo_menu.sp.stat_review') }}</p></div>
			</div>
		</aside>

		<div class="bk-list-main">
			<div class="bk-toolbar">
				<div class="bk-toolbar-actions">
					<router-link to="/registro-alojamiento" class="bk-btn bk-btn--primary"><span v-html="ICON.plus"></span>{{ $t('nuevo_menu.btn.add_space') }}</router-link>
					<router-link to="/my-accommodations" class="bk-btn bk-btn--ghost"><span v-html="ICON.back"></span>{{ $t('nuevo_menu.btn.back_spaces') }}</router-link>
				</div>
				<label class="bk-search">
					<span class="bk-sr-only">{{ $t('nuevo_menu.sp.search_label') }}</span>
					<span v-html="ICON.search"></span>
					<input v-model="busqueda" type="search" :placeholder="$t('nuevo_menu.sp.search')" autocomplete="off" @input="alBuscar" @keydown.enter.prevent="buscarYa">
				</label>
			</div>

			<div v-if="loader" class="bk-loading-box"><HyFLoader variant="inline" /></div>

			<div v-else-if="errorCarga" class="bk-empty" role="alert">
				<div class="bk-empty-ico" aria-hidden="true" v-html="ICON.sync"></div>
				<p class="bk-empty-title">{{ $t('nuevo_menu.sp.error') }}</p>
				<p class="bk-empty-text">{{ $t('nuevo_menu.rv.error_text') }}</p>
				<button type="button" class="bk-btn bk-btn--primary" @click="getAccommodations">{{ $t('nuevo_menu.retry') }}</button>
			</div>

			<div v-else-if="!alojamientos.length" class="bk-empty">
				<template v-if="s">
					<div class="bk-empty-ico" aria-hidden="true" v-html="ICON.search"></div>
					<p class="bk-empty-title">{{ $t('nuevo_menu.sp.not_found') }}</p>
					<p class="bk-empty-text">{{ $t('nuevo_menu.sp.not_found_text') }}</p>
					<button type="button" class="bk-btn bk-btn--ghost" @click="busqueda = ''; buscarYa()">{{ $t('nuevo_menu.sp.clear') }}</button>
				</template>
				<template v-else>
					<div class="bk-empty-ico" aria-hidden="true" v-html="ICON.home"></div>
					<p class="bk-empty-title">{{ $t('nuevo_menu.sp.none') }}</p>
					<p class="bk-empty-text">{{ $t('nuevo_menu.sp.none_text') }}</p>
					<router-link to="/registro-alojamiento" class="bk-btn bk-btn--primary"><span v-html="ICON.plus"></span>{{ $t('nuevo_menu.btn.add_space') }}</router-link>
				</template>
			</div>

			<template v-else>
				<div class="bk-ic-list">
					<article v-for="a in alojamientos" :key="a.idalojamiento" class="bk-ic">
						<div class="bk-ic-img">
							<img v-if="foto(a)" :src="foto(a)" alt="" loading="lazy">
						</div>
						<div>
							<p class="bk-ic-name">{{ nombre(a) }}</p>
							<p class="bk-ic-meta">
								<span class="bk-ic-id">ID {{ a.idalojamiento }}</span>
								<span v-if="a.idcodigo != null && String(a.idcodigo).trim() !== ''" class="bk-ic-id bk-ic-id--code">{{ $t('nuevo_menu.sp.code') }} {{ a.idcodigo }}</span>
								<template v-if="a.direccion">{{ a.direccion }}</template>
							</p>
							<div class="bk-ic-btns">
								<button v-if="!urlIcal(a)" type="button" class="bk-btn bk-btn--ghost" :disabled="generando === `ical-${a.idalojamiento}`" @click="createIcalUrl(a.idalojamiento)">
									<span v-html="ICON.cal"></span>{{ $t('icalendar.create-ical') }}
								</button>
								<button v-if="!urlJson(a)" type="button" class="bk-btn bk-btn--ghost" :disabled="generando === 'json'" @click="createJsonUrl">
									<span v-html="ICON.link"></span>{{ $t('icalendar.create-json') }}
								</button>
								<a class="bk-btn bk-btn--ghost" :href="descargaIcs(a.idalojamiento)" target="_blank" rel="noopener"><span v-html="ICON.download"></span>{{ $t('nuevo_menu.ic.download') }}</a>
								<label class="bk-btn bk-btn--ghost bk-ic-upload">
									<span v-html="ICON.upload"></span>{{ $t('nuevo_menu.ic.upload') }}
									<input :ref="`ics-${a.idalojamiento}`" type="file" accept=".ics,text/calendar" class="bk-sr-only" @change="leerIcs($event, a)">
								</label>
							</div>
						</div>
						<div v-if="urlIcal(a)" class="bk-ic-url">
							<span class="bk-ic-kind">iCal</span><code>{{ urlIcal(a) }}</code>
							<button type="button" class="bk-ib" :aria-label="$t('nuevo_menu.ic.copy_ical')" :data-tip="copiado === `ical-${a.idalojamiento}` ? $t('nuevo_menu.copied') : $t('nuevo_menu.copy')" @click="copiar(urlIcal(a), `ical-${a.idalojamiento}`)" v-html="ICON.copy"></button>
						</div>
						<div v-if="urlJson(a)" class="bk-ic-url">
							<span class="bk-ic-kind">JSON</span><code>{{ urlJson(a) }}</code>
							<button type="button" class="bk-ib" :aria-label="$t('nuevo_menu.ic.copy_json')" :data-tip="copiado === `json-${a.idalojamiento}` ? $t('nuevo_menu.copied') : $t('nuevo_menu.copy')" @click="copiar(urlJson(a), `json-${a.idalojamiento}`)" v-html="ICON.copy"></button>
						</div>
					</article>
				</div>
				<BkPaginacion :actual="paginateData.current_page || 1" :ultima="paginateData.last_page || 1" :total="paginateData.total" @ir="reloadData" />
			</template>
		</div>

		<!-- Confirmar / resultado de subir el .ics -->
		<transition name="bk-modal-fade">
			<div v-if="ics" class="bk-modal" @keydown.esc="cerrarIcs">
				<div class="bk-modal-bg" @click="ics.estado !== 'subiendo' && cerrarIcs()"></div>
				<div class="bk-modal-card" role="dialog" aria-modal="true" aria-labelledby="ic-modal-t">
					<button v-if="ics.estado !== 'subiendo'" type="button" class="bk-modal-x" :aria-label="$t('nuevo_menu.close')" @click="cerrarIcs">×</button>
					<div :class="['bk-modal-ico', { 'bk-modal-ico--danger': ics.estado === 'error' }]" aria-hidden="true" v-html="ICON.cal"></div>
					<h2 id="ic-modal-t" class="bk-modal-title">{{ $t(`nuevo_menu.ic.m_${ics.estado}_t`) }}</h2>
					<p class="bk-modal-text" aria-live="polite">{{ $t(`nuevo_menu.ic.m_${ics.estado}`, { archivo: ics.archivo, nombre: ics.nombre }) }}</p>
					<div class="bk-modal-acts">
						<template v-if="ics.estado === 'confirmar'">
							<button ref="icsNo" type="button" class="bk-btn bk-btn--ghost" @click="cerrarIcs">{{ $t('nuevo_menu.cancel') }}</button>
							<button type="button" class="bk-btn bk-btn--primary" @click="subirIcs">{{ $t('nuevo_menu.ic.m_yes') }}</button>
						</template>
						<button v-else-if="ics.estado !== 'subiendo'" ref="icsOk" type="button" class="bk-btn bk-btn--primary" @click="cerrarIcs">{{ $t('nuevo_menu.ok') }}</button>
						<span v-else class="bk-spinner" style="border-color: rgba(232,100,10,.3); border-top-color: #e8640a; width: 28px; height: 28px;" aria-hidden="true"></span>
					</div>
				</div>
			</div>
		</transition>

		<div v-if="aviso" class="bk-toast-fixed">
			<div :class="['bk-toast', `bk-toast--${aviso.tipo}`]" :role="aviso.tipo === 'error' ? 'alert' : 'status'">
				<span class="bk-toast-icon" aria-hidden="true">{{ aviso.tipo === 'ok' ? '✓' : '!' }}</span>
				<p class="bk-toast-text">{{ aviso.texto }}</p>
				<button type="button" class="bk-toast-close" :aria-label="$t('nuevo_menu.close')" @click="aviso = null">×</button>
			</div>
		</div>
	</div>
</template>

<script>
import axios from "axios";
import BkPaginacion from "@/components/Cuenta/BkPaginacion.vue";
import HyFLoader from "@/components/Loader/HyFLoader.vue";
import ICON from "@/components/Cuenta/iconos";

export default {
	name: "Ical",
	components: { BkPaginacion, HyFLoader },

	data() {
		return {
			ICON,
			api: process.env.VUE_APP_API_URL,
			s3: process.env.VUE_APP_S3_URL || '',
			loader: true,
			errorCarga: false,
			alojamientos: [],
			paginateData: {},
			resumen: { total: 0, aprobados: 0, en_revision: 0 },
			s: "",
			busqueda: "",
			pagina: 1,
			buscarTimer: null,
			generando: null,
			copiado: null,
			// Subida de .ics: { id, nombre, archivo, ical, estado: confirmar|subiendo|ok|error }
			ics: null,
			aviso: null,
			avisoTimer: null,
		};
	},
	computed: {
		// Base de la API sin "api/v1/" (ahí viven /ical y /json).
		base() {
			return this.api.replace("api/v1/", "");
		},
	},
	watch: {
		ics(v, antes) {
			document.body.style.overflow = v ? 'hidden' : '';
			if (v && (!antes || antes.estado !== v.estado)) {
				this.$nextTick(() => { const b = this.$refs.icsNo || this.$refs.icsOk; if (b) b.focus(); });
			}
		},
	},
	methods: {
		avisar(tipo, texto) {
			clearTimeout(this.avisoTimer);
			this.aviso = { tipo, texto };
			this.avisoTimer = setTimeout(() => { this.aviso = null; }, tipo === 'ok' ? 3500 : 6000);
		},
		nombre(a) {
			try { const j = JSON.parse(a.nombre); return j[this.$i18n.locale] || j.es || j.en || Object.values(j)[0] || ''; } catch (e) { return a.nombre || ''; }
		},
		foto(a) {
			const url = a.gallery && a.gallery[0] && a.gallery[0].url;
			if (!url) return '';
			if (url.indexOf('http') === -1) return this.s3 + url;
			const bucket = (process.env.VUE_APP_AWS_BUCKET_URL || '').replace(/^\/+|\/+$/g, '');
			if (!bucket || url.indexOf('s3-sa-east-1.amazonaws.com') === -1 || url.indexOf(bucket) > -1) return url;
			try { const u = new URL(url); u.pathname = `/${bucket}/${u.pathname.replace(/^\/+/, '')}`; return u.toString(); } catch (e) { return url; }
		},
		urlIcal(a) { return a.get_url_ical && a.get_url_ical.url_ical; },
		urlJson(a) { return a.get_url_json && a.get_url_json.url_json; },
		descargaIcs(id) { return `${this.base}ical/${id}`; },

		params() {
			const p = new URLSearchParams();
			if (this.s) p.set('s', this.s);
			if (this.pagina > 1) p.set('page', String(this.pagina));
			return p;
		},
		async getAccommodations() {
			this.loader = true;
			this.errorCarga = false;
			const pedido = (this._pedido || 0) + 1;
			this._pedido = pedido;
			try {
				const p = this.params();
				// Moneda: para buscar por el precio que se ve en Mis espacios.
				p.set('moneda', this.$store.state.exchange.visitorCurrency || 'USD');
				const { data } = await axios.get(`${this.api}accommodations/get/accommodations?${p.toString()}`);
				if (pedido !== this._pedido) return;
				this.alojamientos = data.data || [];
				this.paginateData = data;
				if (data.resumen) this.resumen = data.resumen;
			} catch (e) {
				if (pedido !== this._pedido) return;
				console.error('[Ical] no se pudieron leer los alojamientos', e);
				this.errorCarga = true;
			} finally {
				if (pedido === this._pedido) this.loader = false;
			}
		},
		actualizarUrl() {
			this.$router.replace({ query: Object.fromEntries(this.params()) }).catch(() => {});
		},
		reloadData(page) {
			this.pagina = page;
			this.actualizarUrl();
			window.scrollTo({ top: 0, behavior: "smooth" });
			this.getAccommodations();
		},
		alBuscar() {
			clearTimeout(this.buscarTimer);
			this.buscarTimer = setTimeout(this.buscarYa, 450);
		},
		buscarYa() {
			clearTimeout(this.buscarTimer);
			const s = this.busqueda.trim();
			if (s === this.s) return;
			this.s = s;
			this.pagina = 1;
			this.actualizarUrl();
			this.getAccommodations();
		},

		async createIcalUrl(id) {
			this.generando = `ical-${id}`;
			try {
				const { data } = await axios.post(`${this.base}ical`, { id });
				const a = this.alojamientos.find(x => parseInt(x.idalojamiento) === parseInt(id));
				if (a) this.$set(a, 'get_url_ical', { url_ical: data.url });
				this.avisar('ok', this.$t('nuevo_menu.ic.ical_ok'));
			} catch (e) {
				this.avisar('error', this.$t('nuevo_menu.ic.gen_error'));
			} finally {
				this.generando = null;
			}
		},
		// Genera el JSON de todos los alojamientos del usuario (como antes) y
		// vuelve a leer la lista para mostrar los enlaces.
		async createJsonUrl() {
			this.generando = 'json';
			try {
				await axios.post(`${this.base}json`);
				await this.getAccommodations();
				this.avisar('ok', this.$t('nuevo_menu.ic.json_ok'));
			} catch (e) {
				this.avisar('error', this.$t('nuevo_menu.ic.gen_error'));
			} finally {
				this.generando = null;
			}
		},
		async copiar(texto, clave) {
			try {
				await navigator.clipboard.writeText(texto);
			} catch (e) {
				const ta = document.createElement('textarea');
				ta.value = texto; ta.style.position = 'fixed'; ta.style.opacity = '0';
				document.body.appendChild(ta); ta.select();
				try { document.execCommand('copy'); } catch (e2) { /* sin permiso */ }
				ta.remove();
			}
			this.copiado = clave;
			this.avisar('ok', this.$t('nuevo_menu.ic.copied'));
			setTimeout(() => { if (this.copiado === clave) this.copiado = null; }, 2000);
		},

		// ── Subir un .ics para bloquear fechas ──
		leerIcs(e, a) {
			const archivo = e.target.files && e.target.files[0];
			if (!archivo) return;
			if (!/\.ics$/i.test(archivo.name)) {
				e.target.value = '';
				return this.avisar('error', this.$t('nuevo_menu.ic.bad_file'));
			}
			const fr = new FileReader();
			fr.onload = () => {
				this.ics = { id: a.idalojamiento, nombre: this.nombre(a), archivo: archivo.name, ical: fr.result, estado: 'confirmar' };
			};
			fr.readAsText(archivo);
		},
		async subirIcs() {
			const ics = this.ics;
			this.ics = { ...ics, estado: 'subiendo' };
			try {
				await axios.post(`${this.base}ical/lock-dates-from-ics-file`, { alojamiento_id: ics.id, ical: ics.ical });
				this.ics = { ...ics, estado: 'ok' };
			} catch (e) {
				this.ics = { ...ics, estado: 'error' };
			}
		},
		cerrarIcs() {
			if (!this.ics || this.ics.estado === 'subiendo') return;
			const ref = this.$refs[`ics-${this.ics.id}`];
			const input = Array.isArray(ref) ? ref[0] : ref;
			if (input) input.value = '';
			this.ics = null;
		},
	},
	created() {
		const q = this.$route.query;
		this.s = typeof q.s === 'string' ? q.s : '';
		this.busqueda = this.s;
		this.pagina = Number(q.page) > 0 ? Number(q.page) : 1;
		this.getAccommodations();
		window.scrollTo(0, 0);
	},
	beforeDestroy() {
		clearTimeout(this.buscarTimer);
		clearTimeout(this.avisoTimer);
		document.body.style.overflow = '';
	},
};
</script>

<style src="../../BankInformation/bkCuenta.css"></style>
<style src="../../Cuenta/cuentaMenu.css"></style>
<style>
.bk-ic-btns .bk-btn > span, .bk-ic-upload > span { display: contents; }
.bk-ic-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; margin-top: 4px !important; }
.bk-ic-id { display: inline-flex; align-items: center; min-height: 24px; padding: 0 8px; border-radius: 7px; background: var(--bk-ink); color: #fff; font: 600 12px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
.bk-ic-id--code { background: #faf8f6; color: var(--bk-ink-2); border: 1px solid var(--bk-line-2); }
.bk-ic-upload { position: relative; cursor: pointer; }
.bk-ic-upload:focus-within { box-shadow: var(--bk-ring); border-color: var(--bk-orange); }
.bk-toolbar .bk-btn > span, .bk-search > span:not(.bk-sr-only), .bk-empty .bk-btn > span { display: contents; }
</style>
